import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { CrossChargeBox, OcpiConnector, OcpiLocation, OcpiSession } from '../../../types/responses/Ocpi';

dayjs.extend(utc);

export type Tone = 'ok' | 'info' | 'warn' | 'err' | 'neutral';

/**
 * Mesma semântica de status do resto do dashboard (utils/theme/colors): disponível em verde,
 * em uso em laranja (`busy`), falha em vermelho, indisponível em cinza. Nenhum status usa
 * azul, que ficaria parecido com a cor de tenant. O texto escurece o tom para passar 4,5:1.
 */
export const TONES: Record<Tone, { bg: string; fg: string }> = {
  ok: { bg: '#dff0da', fg: '#245a17' },
  info: { bg: 'rgba(252, 112, 24, 0.12)', fg: '#a8480a' },
  warn: { bg: '#fff4d6', fg: '#6f5200' },
  err: { bg: '#fbe4e3', fg: '#a3231d' },
  neutral: { bg: '#eef0f3', fg: '#444444' },
};

/** Status agregado da estação (StatusEvseOcpiEnum do CS). */
export const LOCATION_STATUS: Record<string, [string, Tone]> = {
  AVAILABLE: ['Available', 'ok'],
  CHARGING: ['In use', 'info'],
  RESERVED: ['Reserved', 'info'],
  BLOCKED: ['Blocked', 'warn'],
  INOPERATIVE: ['Inoperative', 'err'],
  OUTOFORDER: ['Out of order', 'err'],
  PLANNED: ['Planned', 'neutral'],
  REMOVED: ['Removed', 'neutral'],
  UNKNOWN: ['Unknown', 'neutral'],
};

/** Status OCPP do conector, como o CS devolve em `lastStatus.status`. */
export const CONNECTOR_STATUS: Record<string, [string, Tone]> = {
  Available: ['Available', 'ok'],
  Preparing: ['Preparing', 'info'],
  Charging: ['Charging', 'info'],
  SuspendedEV: ['Suspended by vehicle', 'warn'],
  SuspendedEVSE: ['Suspended by station', 'warn'],
  Finishing: ['Connector finishing', 'info'],
  Reserved: ['Reserved', 'info'],
  Faulted: ['Connector faulted', 'err'],
  Unavailable: ['Connector unavailable', 'neutral'],
};

export const SESSION_STATUS: Record<string, [string, Tone]> = {
  ACTIVE: ['In progress', 'info'],
  COMPLETED: ['Completed', 'ok'],
  PENDING: ['Pending', 'warn'],
  INVALID: ['Invalid', 'err'],
  RESERVATION: ['Reservation', 'neutral'],
};

export const statusOf = (
  map: Record<string, [string, Tone]>,
  key?: string,
): { label: string; tone: Tone } => {
  const found = key ? map[key] : undefined;
  return found ? { label: found[0], tone: found[1] } : { label: key || 'Unknown', tone: 'neutral' };
};

export type Period = 'today' | '7d' | '30d';

const CS_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * O CS compara `start_timestamp > dateFrom` e `< dateTo`, no formato `yyyy-MM-dd HH:mm:ss`.
 * O valor vai sem fuso e o CS o lê como UTC, o mesmo fuso do start_timestamp: por isso o
 * período é montado no horário local (o "hoje" do usuário) e enviado convertido para UTC.
 * Recua um segundo no início para não perder a recarga que começou à meia-noite em ponto.
 */
export const periodRange = (period: Period, now: Date = new Date()): { dateFrom: string; dateTo: string } => {
  const end = dayjs(now).add(1, 'minute');
  const days = period === 'today' ? 0 : period === '7d' ? 6 : 29;
  const start = dayjs(now).startOf('day').subtract(days, 'day').subtract(1, 'second');
  return { dateFrom: start.utc().format(CS_DATE_FORMAT), dateTo: end.utc().format(CS_DATE_FORMAT) };
};

export const SESSIONS_PAGE_SIZE = 20;

export const SESSION_FILTERS = ['all', 'ACTIVE', 'PENDING', 'COMPLETED', 'INVALID'] as const;
export type SessionFilter = typeof SESSION_FILTERS[number];

/** Uma página da aba Recargas, consultada no CS (MV2-2120). A página do antd começa em 1. */
export const sessionsPageQuery = (
  range: { dateFrom: string; dateTo: string },
  status: SessionFilter,
  page: number,
) => ({
  ...range,
  status: status === 'all' ? undefined : status,
  offset: (page - 1) * SESSIONS_PAGE_SIZE,
  limit: SESSIONS_PAGE_SIZE,
});

export const formatDateTime = (value?: string): string => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : value;
};

export const formatDate = (value?: string): string => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
};

export const formatMoney = (value?: number, currency = 'BRL'): string => {
  if (value === undefined || value === null) return '—';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

export const formatKwh = (value?: number): string =>
  value === undefined || value === null
    ? '—'
    : `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`;

export const formatHours = (value?: number): string =>
  value === undefined || value === null
    ? '—'
    : `${value.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} h`;

/** Mostra só o começo e o fim da tag, como no restante do dashboard. */
export const maskTag = (tag?: string): string => {
  if (!tag) return '—';
  if (tag.length <= 8) return tag;
  return `${tag.slice(0, 4)}••••${tag.slice(-4)}`;
};

export const connectorTypes = (location: OcpiLocation): string =>
  Array.from(new Set((location.connectors || []).map((c) => c.connectorType).filter(Boolean))).join(' · ');

const CURRENT_TYPES: Record<string, string> = { alternating_current: 'AC', direct_current: 'DC' };

/** O CS manda o tipo de corrente do ConnectorTypeEnum (`alternating_current`/`direct_current`). */
export const currentTypeLabel = (currentType?: string): string =>
  currentType ? CURRENT_TYPES[currentType] || currentType : '—';

/** O CS guarda `powerMax` em watts (o cadastro de estação grava kW × 1000). */
export const formatPower = (watts?: number): string =>
  watts ? `${(watts / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kW` : '—';

/** Em watts, como o `powerMax` dos conectores. */
export const maxPower = (location: OcpiLocation): number =>
  Math.max(0, ...(location.connectors || []).map((c) => c.powerMax || 0));

/** "2 carregando · 1 disponível": resumo por status dos conectores de uma estação. */
export const connectorBreakdown = (
  connectors: OcpiConnector[] = [],
  translate: (key: string) => string = (key) => key,
): string => {
  const counts = new Map<string, number>();
  connectors.forEach((connector) => {
    const { label } = statusOf(CONNECTOR_STATUS, connector.lastStatus?.status);
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([label, n]) => `${n} ${translate(label).toLowerCase()}`)
    .join(' · ');
};

export type ConnectorGroup = 'available' | 'inUse' | 'faulted' | 'unavailable';

const GROUP_OF: Record<string, ConnectorGroup> = {
  Available: 'available',
  Preparing: 'inUse',
  Charging: 'inUse',
  SuspendedEV: 'inUse',
  SuspendedEVSE: 'inUse',
  Finishing: 'inUse',
  Reserved: 'inUse',
  Faulted: 'faulted',
};

export const connectorGroup = (status?: string): ConnectorGroup =>
  (status && GROUP_OF[status]) || 'unavailable';

export const connectorDistribution = (locations: OcpiLocation[]): Record<ConnectorGroup, number> => {
  const result: Record<ConnectorGroup, number> = { available: 0, inUse: 0, faulted: 0, unavailable: 0 };
  locations.forEach((location) =>
    (location.connectors || []).forEach((connector) => {
      result[connectorGroup(connector.lastStatus?.status)] += 1;
    }),
  );
  return result;
};

/** Só Faulted: indisponível e sem status ficam no grupo próprio do quadro de status. */
export const hasFaultedConnector = (location: OcpiLocation): boolean =>
  (location.connectors || []).some((connector) => connectorGroup(connector.lastStatus?.status) === 'faulted');

export const topStationsByEnergy = (
  sessions: OcpiSession[],
  locations: OcpiLocation[],
  size = 5,
): { location: OcpiLocation; kwh: number }[] => {
  const byUuid = new Map<string, number>();
  sessions.forEach((session) => {
    if (!session.chargeBoxUuid) return;
    byUuid.set(session.chargeBoxUuid, (byUuid.get(session.chargeBoxUuid) || 0) + (session.kwh || 0));
  });
  return locations
    .filter((location) => location.chargeBoxUuid && (byUuid.get(location.chargeBoxUuid) || 0) > 0)
    .map((location) => ({ location, kwh: byUuid.get(location.chargeBoxUuid as string) as number }))
    .sort((a, b) => b.kwh - a.kwh)
    .slice(0, size);
};

export const sumBy = <T>(items: T[], pick: (item: T) => number | undefined): number =>
  items.reduce((total, item) => total + (pick(item) || 0), 0);

/** Busca por texto no dashboard, sobre as estações já carregadas. */
export const matchesStation = (location: OcpiLocation, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [location.chargeBoxId, location.chargeBoxUuid, location.street, location.city]
    .filter(Boolean)
    .some((value) => (value as string).toLowerCase().includes(q));
};

export const sortByStartDesc = <T extends { startTimestamp?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => (b.startTimestamp || '').localeCompare(a.startTimestamp || ''));

/**
 * Motivos pelos quais o mk-ms-ocpi-java tira a estação do `/locations` do parceiro
 * (LocationService.hasAddress e hasRequiredFields). Lista vazia: a estação entra no feed.
 */
export const FEED_ISSUE_TEXTS = {
  noAddress: 'No address',
  noCoordinates: 'No coordinates',
  noOperator: 'No operator name',
};

export type FeedIssue = keyof typeof FEED_ISSUE_TEXTS;

const blank = (value: unknown): boolean => value === undefined || value === null || String(value).trim() === '';

export const feedIssues = (station: CrossChargeBox): FeedIssue[] => {
  const issues: FeedIssue[] = [];
  if (!station.address) issues.push('noAddress');
  if (blank(station.location_latitude) || blank(station.location_longitude)) issues.push('noCoordinates');
  if (blank(station.tenant?.name)) issues.push('noOperator');
  return issues;
};

export const groupByOperator = (
  stations: CrossChargeBox[],
): { operator: string; total: number; inFeed: number }[] => {
  const groups = new Map<string, { operator: string; total: number; inFeed: number }>();
  stations.forEach((station) => {
    const operator = station.tenant?.name || '—';
    const group = groups.get(operator) || { operator, total: 0, inFeed: 0 };
    group.total += 1;
    if (!feedIssues(station).length) group.inFeed += 1;
    groups.set(operator, group);
  });
  return Array.from(groups.values()).sort((a, b) => b.total - a.total);
};

/** Busca da aba do parceiro: código, descrição, ID, endereço e cidade da estação. */
export const matchesPartnerStation = (station: CrossChargeBox, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    station.charge_box_code,
    station.charge_box_id,
    station.description,
    station.address?.street,
    station.address?.city,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
};

/** Ponto no mapa, independente de a estação ser enviada (CS) ou recebida (cross). */
export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  title: string;
  tone: Tone;
}

const toPoint = (id: string | undefined, lat: unknown, lng: unknown, title: string, tone: Tone): MapPoint[] => {
  if (!id || lat == null || lng == null || lat === '' || lng === '') return [];
  const point = { id, lat: Number(lat), lng: Number(lng), title, tone };
  return Number.isFinite(point.lat) && Number.isFinite(point.lng) ? [point] : [];
};

/** O título do pino leva o status em texto: recebe o `T` da tela para sair no idioma da lista (MV2-2124). */
export const locationPoints = (
  locations: OcpiLocation[],
  translate: (key: string) => string = (key) => key,
): MapPoint[] =>
  locations.flatMap((l) => {
    const { label, tone } = statusOf(LOCATION_STATUS, l.status);
    return toPoint(l.chargeBoxUuid, l.locationLatitude, l.locationLongitude, `${l.chargeBoxId} · ${translate(label)}`, tone);
  });

/** Recebidas: a cor é se a estação entra no feed do parceiro, não o status do conector. */
export const partnerPoints = (stations: CrossChargeBox[]): MapPoint[] =>
  stations.flatMap((s) =>
    toPoint(
      s.charge_box_id,
      s.location_latitude,
      s.location_longitude,
      `${s.charge_box_code || s.charge_box_id} · ${s.tenant?.name || '—'}`,
      feedIssues(s).length ? 'err' : 'ok',
    ),
  );

/** Estações por página nas listas de enviadas e recebidas. */
export const STATIONS_PAGE_SIZE = 10;
